import React from 'react';
import {
  Box,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import SearchableSelect from './SearchableSelect';
import ExportButton from './ExportButton';

const FilterBar = ({
  filters,
  customers,
  originStates,
  destinationStates,
  shipmentTypes,
  shipmentModes,
  carriers,
  weeks,
  onFilterChange,
  onReset,
  exportData,
  allShipments,
  exportFilename,
  exportSheetName,
  showExport = true,
  customDateRange = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
      <Grid container spacing={1} alignItems='center'>
        {/* Filters Section - Takes 11 columns */}
        <Grid item xs={12} sm={12} md={11} lg={11}>
          <Grid container spacing={1} alignItems='center'>
            {/* Customer Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.5} xl={1.2}>
              <SearchableSelect
                label='Customer'
                value={filters.customer}
                options={customers.filter(c => c !== 'All')}
                onChange={e => onFilterChange('customer', e.target.value)}
                size='small'
                fullWidth
              />
            </Grid>

            {/* Origin Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.2} xl={1}>
              <SearchableSelect
                label='Origin'
                value={filters.originState}
                options={originStates.filter(s => s !== 'All')}
                onChange={e => onFilterChange('originState', e.target.value)}
                size='small'
                fullWidth
              />
            </Grid>

            {/* Destination Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.2} xl={1}>
              <SearchableSelect
                label='Destination'
                value={filters.destinationState}
                options={destinationStates.filter(s => s !== 'All')}
                onChange={e =>
                  onFilterChange('destinationState', e.target.value)
                }
                size='small'
                fullWidth
              />
            </Grid>

            {/* Mode Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.2} xl={1}>
              <SearchableSelect
                label='Mode'
                value={filters.shipmentType}
                options={shipmentTypes.filter(t => t !== 'All')}
                onChange={e => onFilterChange('shipmentType', e.target.value)}
                size='small'
                fullWidth
              />
            </Grid>

            {/* Type Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.4} xl={1.2}>
              <SearchableSelect
                label='Shipment Type'
                value={filters.shipmentTypeRule}
                options={shipmentModes.filter(m => m !== 'All')}
                onChange={e =>
                  onFilterChange('shipmentTypeRule', e.target.value)
                }
                size='small'
                fullWidth
              />
            </Grid>

            {/* Carriers Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.3} xl={1.1}>
              <SearchableSelect
                label='Carriers'
                value={filters.carrier}
                options={carriers.filter(c => c !== 'All')}
                onChange={e => onFilterChange('carrier', e.target.value)}
                size='small'
                fullWidth
              />
            </Grid>

            {/* Date Range Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1.3} xl={1.1}>
              <FormControl fullWidth size='small'>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Date Range</InputLabel>
                <Select
                  value={filters.dateRange}
                  label='Date Range'
                  onChange={e => onFilterChange('dateRange', e.target.value)}
                  sx={{ fontSize: '0.75rem' }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  <MenuItem value='All' sx={{ fontSize: '0.75rem' }}>
                    All Dates
                  </MenuItem>
                  <MenuItem value='last7' sx={{ fontSize: '0.75rem' }}>
                    Last 7 Days
                  </MenuItem>
                  <MenuItem value='last30' sx={{ fontSize: '0.75rem' }}>
                    Last 30 Days
                  </MenuItem>
                  <MenuItem value='last90' sx={{ fontSize: '0.75rem' }}>
                    Last 90 Days
                  </MenuItem>
                  <MenuItem value='custom' sx={{ fontSize: '0.75rem' }}>
                    Custom
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Custom Date Range */}
            {customDateRange && filters.dateRange === 'custom' && (
              <>
                <Grid item xs={12} sm={6} md={3} lg={1.4} xl={1.2}>
                  <TextField
                    label='Start'
                    type='date'
                    size='small'
                    value={filters.startDate || ''}
                    onChange={e => onFilterChange('startDate', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                      sx: { fontSize: '0.75rem' }
                    }}
                    inputProps={{ sx: { fontSize: '0.75rem', py: 0.5 } }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={1.4} xl={1.2}>
                  <TextField
                    label='End'
                    type='date'
                    size='small'
                    value={filters.endDate || ''}
                    onChange={e => onFilterChange('endDate', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                      sx: { fontSize: '0.75rem' }
                    }}
                    inputProps={{ sx: { fontSize: '0.75rem', py: 0.5 } }}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {/* Week Filter */}
            <Grid item xs={12} sm={6} md={3} lg={1.1} xl={1}>
              <SearchableSelect
                label='Week'
                value={filters.week}
                options={weeks.filter(w => w !== 'All')}
                onChange={e => onFilterChange('week', e.target.value)}
                size='small'
                fullWidth
              />
            </Grid>

            {/* Reset Button */}
            <Grid item xs={12} sm={6} md={2} lg={1} xl={0.8}>
              <Button
                variant='contained'
                startIcon={<Refresh sx={{ fontSize: '0.875rem' }} />}
                onClick={onReset}
                size='small'
                fullWidth
                sx={{
                  fontSize: '0.7rem',
                  py: 0.5,
                  whiteSpace: 'nowrap',
                  minWidth: 'auto'
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {/* Export Button - Takes 1 column */}
        {showExport && (
          <Grid
            item
            xs={12}
            sm={12}
            md={1}
            lg={1}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              mt: { xs: 1, md: 0 }
            }}
          >
            <ExportButton
              data={exportData}
              allShipments={allShipments}
              filename={exportFilename}
              sheetName={exportSheetName}
              size='small'
              label='Export'
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default FilterBar;
